<template>
  <div>
    <div class="section-title">
      <p>{{ $i18nPresenter.t('Save as a shortcut') }}</p>
      <aha-badge
        v-if="showBadge"
        label="NEW"
        size="2xs"
        class="bg-primary-90 ml-2.5 cursor-pointer new-badge"
      />
    </div>
    <aha-tooltip
      v-if="isSelected"
      to="google-drive-create-shortcut-button"
      placement="top"
      arrow
      interactive
    >
      <div class="google-drive-tooltip">
        {{ $i18nPresenter.t('You have already saved a shortcut of this presentation to your Google Drive') }}
      </div>
    </aha-tooltip>
    <div class="flex gap-3">
      <div
        name="google-drive-create-shortcut-button"
        class="card-section"
        v-aha-emit-action
      >
        <div v-if="isSelected" class="cursor-pointer absolute top-1 right-1">
          <img
            src="@/assets/images/icon/gg-drive-saved-tick.svg"
            alt=""
            class="box-corner-icon"
          />
        </div>
        <div
          class="badge-option relative flex justify-center align-middle"
          @click="handleLogin('ggdrive')"
          :aria-pressed="isSelected"
          name="google-drive-create-shortcut"
        >
          <img
            src="@/assets/images/icon/gg-drive-icon.svg"
            alt=""
            class="icon"
            v-if="!loadingGoogleDrive"
          >
          <aha-spinner v-else />
          Google Drive
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import * as Sentry from '@sentry/vue'
import configs from '@/config'
import constants from '@/constant'
import Utils from '@/util'
import ConnectorApi from '@/api/connector'
import ConnectorApiV2 from '@/api/connector.v2'

export default {
  name: 'SaveAsGoogleDriveSection',
  props: {
    presentation: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      isSelected: false,
      badgeVisible: true,
      loadingGoogleDrive: false,
    }
  },
  computed: {
    ...mapState({
      currentUser: (state) => state.currentUser,
    }),
    ...mapGetters('featureToggles', [
      'canUseGoogleDriveV2',
    ]),
    showBadge() {
      return this.badgeVisible
    },
    ConnectorApi() {
      return this.canUseGoogleDriveV2 ? ConnectorApiV2 : ConnectorApi
    },
  },
  mounted() {
    this.checkFileExistence()
    this.checkBadgeVisibility()
  },
  methods: {
    _buildData() {
      return {
        serviceType: 'google',
        presentation: {
          id: this.presentation.id,
          title: this.presentation.name,
        },
        // user: {
        //   id: this.currentUser.id,
        //   email: this.currentUser.email,
        // },
        callbackUrl: `${configs.presenterDomain}/presentation/${this.presentation.id}`,
      }
    },
    sendToast(status) {
      const toastName = 'save shortcut in gooogle drive'
      if (status) {
        const toastMessage = `${this.$i18nPresenter.t('Saved')} '${this.presentation.name}' ${this.$i18nPresenter.t('as a shortcut in your Google Drive')}`
        Utils.showToastSuccess(toastMessage, toastName)
        try {
          // try/catch just incase the tracking failed
          Utils.mixpanelTracking(
            constants.eventCategories.PRESENTATION_EDITOR,
            'Save as Shortcut > Google Drive Create Shortcut Button',
            'Google Drive Create Shortcut Button Successful',
            {
              userId: this.currentUser.id,
              presentation: this.presentation.id,
            },
          )
        } catch (error) {
          Sentry.captureException(error)
        }
      } else {
        const toastMessage = `
          ${this.$i18nPresenter.t('Authentication failed. Please authorize to save this presentation to your Google Drive.')} 
        `
        Utils.showToastError(toastMessage, toastName, null, {
          duration: 5000,
          singleton: true,
        })
      }
    },
    async checkFileExistence() {
      try {
        const response = await this.ConnectorApi.checkExistFile(this._buildData())
        this.isSelected = response && response.message == 'exist_file'
      } catch (error) {
        Sentry.captureException(error)
      }
    },
    async handleLogin(integrationType) {
      if (this.loadingGoogleDrive) {
        return
      }
      this.loadingGoogleDrive = true
      const response = await this.ConnectorApi.login(this._buildData())
      if (response && response.status === 'unauthorized') {
        const loginUrl = response.url
        this.newWindow = window.open(loginUrl, '', 'width=500, height=600')
        if (this.newWindow) {
          this.newWindow.document.title = 'Google Authentication'
        }
        this.newWindow.onbeforeunload = () => {
          this.loadingGoogleDrive = false
        }
        window.handleParamsDriver = (params) => {
          const type = Utils.getParamValue('type', params)

          if (type === 'save-file-by-login-success') {
            this.handleSaveToShortCut(integrationType)
          } else if (type === 'callback-failed') {
            this.sendToast(false)
            this.loadingGoogleDrive = false
          }
        }
      }
      if (response && response.message == 'Ok') {
        this.handleSaveToShortCut(integrationType)
      }
    },
    async handleSaveToShortCut(type) {
      if (type === 'ggdrive') {
        try {
          await this.ConnectorApi.saveFile(this._buildData())
          this.isSelected = true
          this.sendToast(true)
        } catch (error) {
          Sentry.captureException(error)
          this.sendToast(false)
        } finally {
          this.loadingGoogleDrive = false
        }
      }
    },
    checkBadgeVisibility() {
      const openCount = localStorage.getItem('ggDriveNewBadgeOpen') || 0
      if (openCount < 3) {
        this.badgeVisible = true
        localStorage.setItem('ggDriveNewBadgeOpen', Number(openCount) + 1)
      } else {
        this.badgeVisible = false
      }
    },
  },
}
</script>

<style scoped lang="scss">
.side-center-modal-share-slide {
  .side-center_title {
    font-size: 14px;
    line-height: 18px;
    //letter-spacing: 0rem;
    text-align: left;
    margin-bottom: 12px;
  }
}
.badge-option {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 8px;
  gap: 4px;
  border-radius: 4px;
  width: 108px;
  height: 68px;
}
.card-section{
  position: relative;
  border-radius: 4px;
  cursor: pointer;
  background: theme('colors.gray.0');
  border: 1px solid theme('colors.gray.40');
  color: theme('colors.gray.100');

  &:not(.disable-card):hover {
    background: theme('colors.purple.10');
    border-color: theme('colors.purple.30');
    color: theme('colors.purple.50');
  }
}

.disable-card {
  background: theme('colors.gray.40');
  border-color: theme('colors.gray.40');
  color: theme('colors.gray.60');
  cursor: not-allowed;
  pointer-events: none;

  img {
    opacity: 0.5;
  }
}

.new-badge {
  margin-left: 2px;
  line-height: 19px;
  position: relative;
  vertical-align: middle;
  height: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  top: 3px;
}

.section-title {
  display: flex;
  gap: 6px;
  margin-bottom: 16px;
  p {
    font-weight: bold;
    font-size: 14px;
    line-height: 20px;
    font-weight: 700;
    color: #282E3E;
  }
}

.google-drive-tooltip {
  text-align: left;
  width: 228px;
}
</style>
